import React, { useState, useEffect } from "react";
import { encryptData, decryptData } from "./Helper";
import { IconClock, IconCheck, IconChevronLeft, IconChevronRight, IconFlag } from "@tabler/icons-react";

const QuizActivePage = ({
  quizData,
  onAnswerSelect,
  onComplete,
  guestUser,
}) => {

  const isGuest = !!guestUser;
  const quizId = quizData?.id || "temp";
  const guestId = guestUser?.guest_id || "default";

  const STORAGE_KEY = isGuest
    ? `quiz_progress_guest_${quizId}_${guestId}`
    : `quiz_progress_${quizId}`;

  const getDecryptedStorage = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    try {
      return decryptData(saved);
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const decrypted = getDecryptedStorage();
    return decrypted?.currentIndex || 0;
  });

  const [selectedAnswers, setSelectedAnswers] = useState(() => {
    const decrypted = getDecryptedStorage();
    return decrypted?.answers || {};
  });

  const [timeLeft, setTimeLeft] = useState(() => {
    const defaultTime = (quizData?.timeLimit || 30) * 60;
    const decrypted = getDecryptedStorage();
    return decrypted?.timeLeft || defaultTime;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!quizData) return;
    const progress = {
      currentIndex: currentQuestionIndex,
      answers: selectedAnswers,
      timeLeft,
    };
    const encrypted = encryptData(progress);
    localStorage.setItem(STORAGE_KEY, encrypted);
  }, [currentQuestionIndex, selectedAnswers, timeLeft, STORAGE_KEY, quizData]);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleFinalSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  if (!quizData) {
    return <div className="p-5 text-center">Loading assessment details...</div>;
  }

  if (!quizData.questions || quizData.questions.length === 0) {
    return <div className="p-5 text-center">No questions found.</div>;
  }

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      localStorage.removeItem(STORAGE_KEY);
      await onComplete();
    } catch (error) {
      console.error("Submission failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const currentQuestion = quizData.questions[currentQuestionIndex];
  const totalQuestions = quizData.questions.length;
  const progressPercent = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleOptionSelect = async (optionId) => {
    const updatedAnswers = {
      ...selectedAnswers,
      [currentQuestion.id]: optionId,
    };
    setSelectedAnswers(updatedAnswers);
    if (onAnswerSelect) {
      await onAnswerSelect(currentQuestion.id, optionId);
    }
  };

  return (
    <div className="bg-light p-4 rounded-3" style={{ minHeight: "650px" }}>
      <div className="mx-auto" style={{ maxWidth: "800px" }}>
        {/* Header Section */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 bg-white p-3 rounded-3 shadow-sm">
          <div>
            <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                <span className="badge bg-blue-lt mr-2">Q{currentQuestionIndex + 1}</span>
                {quizData.title}
            </h4>
            <div className="text-muted small fw-medium">
              Assessment Progress: {currentQuestionIndex + 1} / {totalQuestions}
            </div>
          </div>

          <div
            className={`badge ${timeLeft < 60 ? "bg-red" : "bg-blue"} d-flex align-items-center gap-2 py-2 px-3 shadow-sm`}
            style={{ fontSize: "1.4rem", fontWeight: "800", letterSpacing: '1px' }}
          >
            <IconClock size={24} />
            {formatTime(timeLeft)}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress mb-4 bg-white shadow-sm border" style={{ height: "12px", borderRadius: "20px" }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: `${progressPercent}%`, transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)", borderRadius: "20px" }}
          ></div>
        </div>

        {/* Guest User Warning */}
        {isGuest && (
          <div className="alert alert-info py-2 d-flex align-items-center gap-2 mb-4 border-0 shadow-sm rounded-3">
             <IconCheck size={20} className="text-info" />
             <span className="small">Taking as Guest: <strong>{guestUser.name}</strong> ({guestUser.email})</span>
          </div>
        )}

        {/* Question Card */}
        <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
          <div className="card-body p-4 p-md-5">
            <h4 className="text-dark fw-bold mb-5 leading-tight text-center px-lg-4 ">
              {currentQuestion.question_text}
            </h4>

            <div className="row g-3">
              {currentQuestion.question_options?.map((option) => {
                const isSelected = selectedAnswers[currentQuestion.id] === option.id;
                return (
                  <div key={option.id} className="col-12 col-md-6">
                    <div
                      onClick={() => handleOptionSelect(option.id)}
                      className={`p-3 h-100 border rounded-3 d-flex align-items-center transition-all cursor-pointer quiz-option ${
                        isSelected ? "border-primary bg-primary-lt" : "bg-white border-light-subtle"
                      }`}
                      style={{ borderWidth: "2px", minHeight: '80px' }}
                    >
                      <div
                        className={`rounded-circle border me-3 d-flex align-items-center justify-content-center flex-shrink-0 mr-2 ${
                          isSelected ? "bg-primary text-white border-primary" : "bg-white border-2"
                        }`}
                        style={{ width: "28px", height: "28px" }}
                      >
                        {isSelected && <IconCheck size={16} stroke={3} />}
                      </div>
                      <span className={`fs-4 ${isSelected ? "text-primary fw-bold" : "text-dark fw-medium"}`}>
                        {option.option_text}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <button
            className="btn btn-outline-secondary px-4 py-2 d-flex align-items-center gap-1 fw-bold rounded-pill"
            disabled={currentQuestionIndex === 0}
            onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
          >
            <IconChevronLeft size={18} /> Previous
          </button>

          {currentQuestionIndex === totalQuestions - 1 ? (
            <button
              className="btn btn-success px-5 py-2 d-flex align-items-center gap-2 fw-bold rounded-pill shadow-sm"
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
            >
              <IconFlag size={20} />
              {isSubmitting ? "Submitting..." : "Finish & Submit"}
            </button>
          ) : (
            <button
              className="btn btn-primary px-5 py-2 d-flex align-items-center gap-2 fw-bold rounded-pill shadow-sm"
              onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
            >
              Next <IconChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizActivePage;
